import {
  ContentLayout,
  CustomTable,
  DeleteModal,
  useAppConfig,
  useFormatAmount,
} from "../../../shared";
import * as React from "react";
import {
  Button,
  Chip,
  Stack,
  Tooltip,
  Box,
  Checkbox,
  TextField,
  ListItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { sortItems } from "../../../utils";
import { useSurcharges } from "../hooks/useSurcharges";
import InputControl from "../../../shared/components/InputControl";

export const SurchargesList = () => {
  const navigate = useNavigate();
  const {
    state,
    isEditable,
    isDeletable,
    isCreateVisible,
    taxState,
    globalData,
    handleFilter,
    handleApplyFilter,
    handleFilterClear,
    filteredSurchargesList,
    pendingFilterChips,
    // onChangeFilter,
    handleModal,
    handleDeleteSurCharges,
  } = useSurcharges({
    load: true,
    isList: false,
  });

  const normalizeAmount = useFormatAmount();
  const { currencyPrecision } = useAppConfig();

  const salesChannels = React.useMemo(() => {
    return globalData?.sale_channels?.filter((channel) =>
      ["pos", "online ordering"].includes(channel.label.toLowerCase()),
    );
  }, [globalData?.sale_channels]);

  const ActionButtons = ({ id, deleteAction }) => {
    return (
      <div className="action-buttons">
        {isEditable && (
          <Link to={`edit/${id}`}>
            <img alt="edit" src="/icons/ic_edit.svg" />
          </Link>
        )}
        {isDeletable && (
          <img alt="delete" onClick={deleteAction} src="/icons/ic_delete.svg" />
        )}
      </div>
    );
  };
  const columns = React.useMemo(
    () => [
      {
        Header: "Title",
        accessor: "name",
        sortType: (prev, curr, columnId) => {
          return sortItems(prev, curr, columnId);
        },
      },
      {
        Header: "Charge",
        accessor: "amount",
        Cell: ({ value, row }) => {
          const isPercentage = row.original.fee_type === "percentage";

          if (isPercentage) {
            // Remove unnecessary trailing zeros
            const formatted = Number.isInteger(value)
              ? value
              : value.toFixed(currencyPrecision);
            return `${formatted}%`;
          }

          // return `$${Number(value).toFixed(2)}`;
          return normalizeAmount(value);
        },
        disableGlobalFilter: false,
      },
      {
        Header: "Sale Channel",
        accessor: "channels",
        disableGlobalFilter: false,
        disableSortBy: true,
        Cell: ({ value }) => {
          return (
            <Stack
              sx={{
                flexDirection: "row",
                flexWrap: "wrap",
                gap: "4px",
              }}
            >
              {value?.slice(0, 2).map((data) => {
                return (
                  <Chip
                    sx={{ mr: 1, borderRadius: "8px" }}
                    label={data.name}
                    variant="filled"
                    size="small"
                  />
                );
              })}
              {value?.length > 2 && (
                <Tooltip
                  title={
                    <Box
                      component="ul"
                      sx={{
                        margin: 0,
                        padding: "4px 8px",
                        listStyle: "none",
                      }}
                    >
                      {value?.slice(2).map((data, index) => (
                        <li key={index}>{data.name}</li>
                      ))}
                    </Box>
                  }
                  slotProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: "#192029",
                      },
                    },
                    arrow: {
                      sx: {
                        color: "#192029",
                      },
                    },
                  }}
                  arrow
                >
                  <Chip
                    sx={{ borderRadius: "8px" }}
                    label={`+${value?.length - value?.slice(0, 2)?.length}`}
                    variant="filled"
                    size="small"
                  />
                </Tooltip>
              )}
            </Stack>
          );
        },
      },
      {
        Header: "Sale Type",
        accessor: "sale_types",
        disableGlobalFilter: false,
        disableSortBy: true,
        Cell: ({ value }) => {
          return (
            <Stack
              sx={{
                flexDirection: "row",
                flexWrap: "wrap",
                gap: "4px",
              }}
            >
              {value?.slice(0, 2).map((data) => {
                return (
                  <Chip
                    sx={{ mr: 1, borderRadius: "8px" }}
                    label={data.name}
                    variant="filled"
                    size="small"
                  />
                );
              })}
              {value?.length > 2 && (
                <Tooltip
                  title={
                    <Box
                      component="ul"
                      sx={{
                        margin: 0,
                        padding: "4px 8px",
                        listStyle: "none",
                      }}
                    >
                      {value?.slice(2).map((data, index) => (
                        <li key={index}>{data.name}</li>
                      ))}
                    </Box>
                  }
                  slotProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: "#192029",
                      },
                    },
                    arrow: {
                      sx: {
                        color: "#192029",
                      },
                    },
                  }}
                  arrow
                >
                  <Chip
                    sx={{ borderRadius: "8px" }}
                    label={`+${value?.length - value?.slice(0, 2)?.length}`}
                    variant="filled"
                    size="small"
                  />
                </Tooltip>
              )}
            </Stack>
          );
        },
      },
      {
        Header: "Tax Group",
        accessor: "tax_group_name",
        disableGlobalFilter: false,
      },
      {
        Header: "Action",
        id: "action",
        align: "right",
        disableGlobalFilter: true,
        disableSortBy: true,
        accessor: (row, index) => {
          return (
            <ActionButtons
              id={row.id}
              deleteAction={() => handleModal(true, row.id)}
            />
          );
        },
      },
    ],
    [taxState],
  );

  return (
    <>
      <ContentLayout
        title="Surcharges"
        rightContent={
          <>
            {isCreateVisible && (
              <Button
                type="button"
                size="medium"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate("/surcharges/create")}
              >
                New Surcharge
              </Button>
            )}
          </>
        }
      >
        <CustomTable
          columns={columns}
          isLoading={state?.isBusy}
          data={filteredSurchargesList}
          defaultSearch={true}
          tableId="surcharges"
          filterApplied={pendingFilterChips}
          onFilterClear={(chip) => handleFilterClear("single", chip)}
          onAllFilterClear={() => handleFilterClear("all")}
          filterSection={
            <SurchargesFilterSection
              filters={state.filters}
              salesChannels={salesChannels}
              saleTypes={globalData?.sale_type}
              handleFilter={handleFilter}
              handleApplyFilter={handleApplyFilter}
            />
          }
        />
        <DeleteModal
          open={state.deleteModalOpen}
          handleClose={() => handleModal(false, null)}
          onConfirm={handleDeleteSurCharges}
        />
      </ContentLayout>
    </>
  );
};

const SurchargesFilterSection = ({
  filters,
  salesChannels = [],
  saleTypes = [],
  handleFilter,
  handleApplyFilter,
}) => {
  return (
    <Stack flexDirection="row" width="100%" gap="16px" alignItems="center">
      <InputControl
        type="dropdown"
        multiple
        options={salesChannels}
        value={filters.filter.channel}
        onChange={(_, value) =>
          handleFilter({ name: "channel", value: value ?? [] })
        }
        getOptionLabel={(option) => option.label ?? ""}
        isOptionEqualToValue={(option, val) => option.value === val.value}
        disableCloseOnSelect
        disableClearable={true}
        renderTags={(value) => null}
        renderOption={(props, option, { selected }) => (
          <ListItem {...props} key={option.value} disableGutters disablePadding>
            <Checkbox
              style={{ marginRight: 8 }}
              checked={selected}
              size="small"
            />
            {option.label}
          </ListItem>
        )}
        renderInput={(params) => (
          <TextField {...params} label="Sales Channel" />
        )}
        sx={{ maxWidth: 220 }}
      />
      <InputControl
        type="dropdown"
        multiple
        options={saleTypes}
        value={filters.filter.saleType}
        onChange={(_, value) =>
          handleFilter({ name: "saleType", value: value ?? [] })
        }
        getOptionLabel={(option) => option.label ?? ""}
        isOptionEqualToValue={(option, val) => option.value === val.value}
        disableCloseOnSelect
        disableClearable={true}
        renderTags={(value) => null}
        renderOption={(props, option, { selected }) => (
          <ListItem {...props} key={option.value} disableGutters disablePadding>
            <Checkbox
              style={{ marginRight: 8 }}
              checked={selected}
              size="small"
            />
            {option.label}
          </ListItem>
        )}
        renderInput={(params) => <TextField {...params} label="Sale Type" />}
        sx={{ maxWidth: 190 }}
      />
      <Box sx={{ width: "100%" }} />
      <Button
        variant="contained"
        size="small"
        onClick={handleApplyFilter}
        disabled={!filters.isFilterApplied}
      >
        Apply
      </Button>
    </Stack>
  );
};
